/**
 * Progressive Overview Dashboard data hook.
 * Week tiles load from staged GET /api/dashboard/period-metrics (not fat plan-vs-actual).
 * Summary / holidays / employees settle independently and are not refetched on week change.
 *
 * To revert to the old Promise.all + plan-vs-actual behaviour:
 *   import { … } from './useOverviewDashboardData.sequential';
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, subWeeks } from 'date-fns';
import {
  dashboardApi,
  employeeApi,
  holidayRequestApi,
  type DashboardPeriodMetricsDto,
  type DashboardSummary,
} from '../../services/api';
import { useLocationContext } from '../../components/Layout';
import { useWeekStartDay } from '../../hooks/useWeekStartDay';
import type { Employee, HolidayRequest } from '../../types';
import type { KpiCardData, LeaveAbsenceItem, OverviewDashboardData, OverviewSectionLoading } from './types';
import {
  buildComplianceAlerts,
  buildReportsNeedingAttention,
  CHART_COLORS,
  formatWeekRangeLabel,
  getWeekBounds,
} from '../../utils/overviewDashboardMetrics';

function formatEuro(value: number): string {
  return `€${Math.round(value).toLocaleString('en-IE')}`;
}

function pctChange(current: number, previous: number): { text: string; color: string } {
  if (previous <= 0 && current <= 0) return { text: 'No change vs last period', color: 'text-gray-500' };
  if (previous <= 0) return { text: '↑ vs last period', color: 'text-emerald-600' };
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? '↑' : '↓';
  const color = change >= 0 ? 'text-emerald-600' : 'text-red-500';
  return { text: `${sign} ${Math.abs(change).toFixed(1)}% vs last period`, color };
}

function countChange(current: number, previous: number, invertGood = false): { text: string; color: string } {
  const diff = current - previous;
  if (diff === 0) return { text: 'No change vs last period', color: 'text-gray-500' };
  const improved = invertGood ? diff < 0 : diff > 0;
  const sign = diff > 0 ? '↑' : '↓';
  const color = improved ? 'text-emerald-600' : 'text-red-500';
  return { text: `${sign} ${Math.abs(diff)} vs last period`, color };
}

function buildKpiCardsFromPeriod(
  current: DashboardPeriodMetricsDto,
  previous: DashboardPeriodMetricsDto | null | undefined,
): KpiCardData[] {
  const c = current.kpis;
  const p = previous?.kpis;
  const costTrend = pctChange(c.actualCost, p?.actualCost ?? 0);
  const underOver = c.planCost - c.actualCost;
  const planSubtext =
    underOver >= 0
      ? `${formatEuro(underOver)} under plan`
      : `${formatEuro(Math.abs(underOver))} over plan`;
  const planColor = underOver >= 0 ? 'text-emerald-600' : 'text-red-500';
  const attendanceTrend = p
    ? pctChange(c.attendancePct, p.attendancePct)
    : { text: 'This period', color: 'text-gray-500' };

  return [
    {
      title: 'Total Labour Cost',
      value: formatEuro(c.actualCost),
      trend: costTrend.text,
      trendColor: costTrend.color,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      icon: 'euro',
    },
    {
      title: 'Planned vs Actual Labour',
      value: `${c.planVsActualPct}%`,
      trend: planSubtext,
      trendColor: planColor,
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      icon: 'bar',
    },
    {
      title: 'Attendance Rate',
      value: `${c.attendancePct}%`,
      trend: attendanceTrend.text,
      trendColor: attendanceTrend.color,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      icon: 'users',
    },
    {
      title: 'Late Arrivals',
      value: String(c.lateCount),
      trend: countChange(c.lateCount, p?.lateCount ?? c.lateCount, true).text,
      trendColor: countChange(c.lateCount, p?.lateCount ?? c.lateCount, true).color,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      icon: 'late',
    },
    {
      title: 'Early Leaves',
      value: String(c.earlyCount),
      trend: countChange(c.earlyCount, p?.earlyCount ?? c.earlyCount, true).text,
      trendColor: countChange(c.earlyCount, p?.earlyCount ?? c.earlyCount, true).color,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      icon: 'clock',
    },
    {
      title: 'Overtime Hours',
      value: String(Math.round(c.otHours * 10) / 10),
      trend: pctChange(c.otHours, p?.otHours ?? 0).text,
      trendColor: pctChange(c.otHours, p?.otHours ?? 0).color,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      icon: 'overtime',
    },
  ];
}

function leaveFromPeriod(
  metrics: DashboardPeriodMetricsDto | null,
  summary: DashboardSummary | null,
): LeaveAbsenceItem[] {
  const leave = metrics?.leave ?? { annual: 0, sick: 0, other: 0 };
  return [
    { label: 'Annual Leave', count: leave.annual, icon: 'calendar', color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Sick Leave', count: leave.sick, icon: 'heart', color: 'text-rose-600', bg: 'bg-rose-50' },
    { label: 'Other Leave', count: leave.other, icon: 'user-x', color: 'text-violet-600', bg: 'bg-violet-50' },
    {
      label: 'Absent Today',
      count: summary?.absentToday.length ?? 0,
      icon: 'x-circle',
      color: 'text-orange-600',
      bg: 'bg-orange-50',
    },
  ];
}

export function useOverviewDashboardData(currentWeek: Date) {
  const weekStartsOn = useWeekStartDay();
  const { debouncedSelectedLocationId } = useLocationContext();

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [holidayRequests, setHolidayRequests] = useState<HolidayRequest[]>([]);
  const [employeeDirectory, setEmployeeDirectory] = useState<Employee[]>([]);
  const [periodMetrics, setPeriodMetrics] = useState<DashboardPeriodMetricsDto | null>(null);
  const [previousPeriodMetrics, setPreviousPeriodMetrics] = useState<DashboardPeriodMetricsDto | null>(null);
  const [loadedPeriodStart, setLoadedPeriodStart] = useState<string | null>(null);

  const [sectionLoading, setSectionLoading] = useState<OverviewSectionLoading>({
    week: true,
    previousWeek: true,
    summary: true,
    holidays: true,
    employees: true,
  });

  const [weekError, setWeekError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const latestWeekRef = useRef({ currentWeek, weekStartsOn, locationId: debouncedSelectedLocationId });
  latestWeekRef.current = {
    currentWeek,
    weekStartsOn,
    locationId: debouncedSelectedLocationId,
  };

  const bounds = useMemo(
    () => getWeekBounds(currentWeek, weekStartsOn),
    [currentWeek, weekStartsOn],
  );

  const setLoadingFlag = useCallback((key: keyof OverviewSectionLoading, value: boolean) => {
    setSectionLoading((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
  }, []);

  const fetchSummary = useCallback(
    async (isCancelled?: () => boolean) => {
      setLoadingFlag('summary', true);
      setSummaryError(null);
      try {
        const data = await dashboardApi.getSummary(debouncedSelectedLocationId);
        if (isCancelled?.()) return;
        setSummary(data);
      } catch (err) {
        if (isCancelled?.()) return;
        console.error('Overview summary load failed:', err);
        setSummaryError('Unable to load alerts summary.');
      } finally {
        if (!isCancelled?.()) setLoadingFlag('summary', false);
      }
    },
    [debouncedSelectedLocationId, setLoadingFlag],
  );

  const fetchHolidays = useCallback(
    async (isCancelled?: () => boolean) => {
      setLoadingFlag('holidays', true);
      try {
        const data = await holidayRequestApi.getAll();
        if (isCancelled?.()) return;
        setHolidayRequests(data);
      } catch (err) {
        if (isCancelled?.()) return;
        console.error('Overview holidays load failed:', err);
      } finally {
        if (!isCancelled?.()) setLoadingFlag('holidays', false);
      }
    },
    [setLoadingFlag],
  );

  const fetchEmployees = useCallback(
    async (isCancelled?: () => boolean) => {
      setLoadingFlag('employees', true);
      try {
        // Slim picker — not full /employees (was ~25s / 350KB and starved week APIs).
        const data = await employeeApi.getPickerAsEmployees();
        if (isCancelled?.()) return;
        setEmployeeDirectory(data);
      } catch (err) {
        if (isCancelled?.()) return;
        console.error('Overview employees load failed:', err);
      } finally {
        if (!isCancelled?.()) setLoadingFlag('employees', false);
      }
    },
    [setLoadingFlag],
  );

  const fetchPeriodMetrics = useCallback(
    async (isCancelled?: () => boolean, bypassCache = false) => {
      const { currentWeek: week, weekStartsOn: startDay, locationId } = latestWeekRef.current;
      const { apiWeekStart } = getWeekBounds(week, startDay);
      const periodStart = format(apiWeekStart, 'yyyy-MM-dd');

      setLoadingFlag('week', true);
      setLoadingFlag('previousWeek', true);
      setWeekError(null);
      // Keep previous tiles visible while loading (avoid zero-flash on cold weeks).
      try {
        const response = await dashboardApi.getPeriodMetrics({
          periodType: 'Week',
          periodStart,
          locationId,
          includePrevious: true,
          bypassCache,
        });
        if (isCancelled?.()) return;
        setPeriodMetrics(response.current);
        setPreviousPeriodMetrics(response.previous ?? null);
        setLoadedPeriodStart(periodStart);
      } catch (err) {
        if (isCancelled?.()) return;
        console.error('Overview period metrics load failed:', err);
        setWeekError('Unable to load week metrics. Please try again.');
        setPeriodMetrics(null);
        setPreviousPeriodMetrics(null);
        setLoadedPeriodStart(null);
      } finally {
        if (!isCancelled?.()) {
          setLoadingFlag('week', false);
          setLoadingFlag('previousWeek', false);
        }
      }
    },
    [setLoadingFlag],
  );

  useEffect(() => {
    let cancelled = false;
    void fetchSummary(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchSummary]);

  useEffect(() => {
    let cancelled = false;
    void fetchHolidays(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchHolidays]);

  useEffect(() => {
    // Defer directory until after first paint / metrics path has started.
    let cancelled = false;
    const t = setTimeout(() => {
      void fetchEmployees(() => cancelled);
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [fetchEmployees]);

  useEffect(() => {
    let cancelled = false;
    void fetchPeriodMetrics(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [currentWeek, weekStartsOn, debouncedSelectedLocationId, fetchPeriodMetrics]);

  const refresh = useCallback(async () => {
    await Promise.allSettled([
      fetchSummary(),
      fetchHolidays(),
      fetchEmployees(),
      fetchPeriodMetrics(undefined, true),
    ]);
  }, [fetchSummary, fetchHolidays, fetchEmployees, fetchPeriodMetrics]);

  const currentPeriodStart = format(bounds.apiWeekStart, 'yyyy-MM-dd');
  const metricsMatchWeek = loadedPeriodStart === currentPeriodStart;
  const displayMetrics = metricsMatchWeek ? periodMetrics : null;
  const displayPrevious = metricsMatchWeek ? previousPeriodMetrics : null;

  const data: OverviewDashboardData | null = useMemo(() => {
    const reports = buildReportsNeedingAttention(summary);
    if (displayMetrics && displayMetrics.payrollReadiness.pending > 0) {
      const unapproved = displayMetrics.payrollReadiness.pending;
      reports.unshift({
        id: 'unapproved-timecards',
        title: `${unapproved} Unapproved Timecard${unapproved === 1 ? '' : 's'}`,
        description: 'Pending manager approval for this period',
        icon: 'file',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        href: '/timecards',
        hrefLabel: 'Open Timecards',
      });
    }

    if (!displayMetrics) {
      return {
        dateRangeLabel: formatWeekRangeLabel(bounds.weekStart, bounds.weekEnd),
        kpiCards: [],
        labourCostChart: [],
        locationCosts: [],
        departmentCosts: [],
        totalLabourCost: 0,
        reportsNeedingAttention: reports.slice(0, 5),
        leaveAbsence: leaveFromPeriod(null, summary),
        payrollReadiness: { percent: 0, approved: 0, pending: 0, rejected: 0, total: 0 },
        complianceAlerts: buildComplianceAlerts(summary),
        employeeSpotlight: [],
        employeeOptions: [],
        getEmployeeStats: () => [],
      };
    }

    const locationCosts = displayMetrics.series.byLocation.map((item, i) => ({
      name: item.name,
      value: item.value,
      percent: item.percent,
      color: item.color ?? CHART_COLORS[i % CHART_COLORS.length],
    }));
    const departmentCosts = displayMetrics.series.byDepartment.map((item, i) => ({
      name: item.name,
      value: item.value,
      percent: item.percent,
      color: item.color ?? CHART_COLORS[i % CHART_COLORS.length],
    }));

    return {
      dateRangeLabel: formatWeekRangeLabel(bounds.weekStart, bounds.weekEnd),
      kpiCards: buildKpiCardsFromPeriod(displayMetrics, displayPrevious),
      labourCostChart: displayMetrics.series.byDay,
      locationCosts,
      departmentCosts,
      totalLabourCost: displayMetrics.kpis.actualCost,
      reportsNeedingAttention: reports.slice(0, 5),
      leaveAbsence: leaveFromPeriod(displayMetrics, summary),
      payrollReadiness: displayMetrics.payrollReadiness,
      complianceAlerts: buildComplianceAlerts(summary),
      employeeSpotlight: [],
      employeeOptions: employeeDirectory.slice(0, 200).map((e) => ({
        id: e.id,
        name: `${e.firstName} ${e.lastName}`.trim(),
        role: '',
        initials: `${e.firstName?.[0] ?? ''}${e.lastName?.[0] ?? ''}`.toUpperCase() || '?',
        avatarColor: 'bg-blue-500',
        profileImageUrl: e.profileImageUrl,
        updatedAt: e.updatedAt,
      })),
      getEmployeeStats: () => [],
    };
  }, [displayMetrics, displayPrevious, bounds, summary, employeeDirectory]);

  const error = weekError ?? summaryError;
  // Skeletons while selected week metrics are not ready (never show another week's numbers as zeros).
  const weekLoading = sectionLoading.week || !metricsMatchWeek;
  const loading = weekLoading && !displayMetrics;

  return {
    data,
    loading,
    sectionLoading: {
      ...sectionLoading,
      week: weekLoading,
      previousWeek: weekLoading,
    },
    error,
    refresh,
    weekStart: bounds.weekStart,
    weekEnd: bounds.weekEnd,
    weekStartsOn,
    summary,
    holidayRequests,
    employeeDirectory,
    currentReport: null,
    locationId: debouncedSelectedLocationId,
  };
}

export function useOverviewWeekNavigation(initialWeek = new Date()) {
  const [currentWeek, setCurrentWeek] = useState(initialWeek);
  const weekStartsOn = useWeekStartDay();

  return {
    currentWeek,
    weekStartsOn,
    goToPreviousWeek: () => setCurrentWeek((w) => subWeeks(w, 1)),
    goToNextWeek: () => setCurrentWeek((w) => subWeeks(w, -1)),
    goToCurrentWeek: () => setCurrentWeek(new Date()),
    setCurrentWeek,
  };
}
